import { isMatch } from 'date-fns'
import { isNil } from 'es-toolkit'
import type { SingleParserBuilder } from 'nuqs'

// biome-ignore lint/suspicious/noExplicitAny: nuqs parsers are heterogeneous by URL-state key.
type UrlStateSchema<T> = Record<keyof T, SingleParserBuilder<any>>

export function normalizeDefaultState<T extends Record<string, unknown>>(
  defaultState: T,
  schema: UrlStateSchema<T>
): T
export function normalizeDefaultState<T extends Record<string, unknown>>(
  defaultState: T | undefined,
  schema: UrlStateSchema<T>
): T | undefined
export function normalizeDefaultState<T extends Record<string, unknown>>(
  defaultState: T | undefined,
  schema: UrlStateSchema<T>
): T | undefined {
  if (isNil(defaultState)) return defaultState

  const result = { ...defaultState }

  Object.keys(schema).forEach(key => {
    const typedKey = key as keyof T
    result[typedKey] = normalizeDefaultValue(defaultState[typedKey], schema[typedKey])
  })

  return result
}

// biome-ignore lint/suspicious/noExplicitAny: parser output types differ per key and are preserved by the caller's schema.
const normalizeDefaultValue = <T>(value: T, parser: SingleParserBuilder<any>): T => {
  if (isNil(value) || typeof value !== 'string') return value

  try {
    const wireValue = isDateLikeString(value) ? parser.serialize(value) : value
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- nuqs parser output is any
    return parser.parse(wireValue) as T
  } catch {
    try {
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- nuqs parser output is any
      return parser.parse(value) as T
    } catch {
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- T is the caller's per-key value type
      return isDateLikeString(value) ? (null as T) : value
    }
  }
}

const DATE_LIKE_FORMATS = [
  'yyyy-MM-dd',
  "yyyy-MM-dd'T'HH:mmXXX",
  "yyyy-MM-dd'T'HH:mm:ssXXX",
  "yyyy-MM-dd'T'HH:mm:ss.SSSXXX",
] as const

const isDateLikeString = (value: string) => DATE_LIKE_FORMATS.some(format => isMatch(value, format))
